/**
 * SF-5082 — the settings under "Automatic drafts and replies" (auto_send) on the Help Desk tab:
 *  - `eva_helpdesk_priority_guide`: what high / medium / low priority mean for this company,
 *    given to Eva on every ticket. Blank = today's rule (shown as the placeholder). One Save
 *    button sends all three, trimmed.
 *  - `eva_helpdesk_auto_send_priorities`, `_reply_types`, `_categories`: what Eva may send on her
 *    own. Each tick saves that one list straight away, like the reply level. An empty list is
 *    allowed and means Eva sends nothing by herself.
 * Missing on the company = the default (today's behaviour). High, an apology and the safety
 * categories can never be ticked (sf-api refuses them too); they are shown so staff know why.
 *
 * The parent shows this only while replies are on in effect and the saved level is auto_send,
 * and passes its lock (edit rights, company list, another save running); the reason is in the
 * row's note above, as for the sign-off.
 */
import { useEffect, useState } from 'react';
import { Button, Checkbox, Input, Space, Typography } from 'antd';
import type { EvaHelpdeskRepliesFlagProps } from './EvaHelpdeskRepliesFlag';
import {
  AUTO_SEND_CATEGORY_OPTIONS,
  AUTO_SEND_PRIORITY_OPTIONS,
  AUTO_SEND_REPLY_TYPE_OPTIONS,
  DEFAULT_AUTO_SEND_CATEGORIES,
  DEFAULT_AUTO_SEND_PRIORITIES,
  DEFAULT_AUTO_SEND_REPLY_TYPES,
  PRIORITY_GUIDE_FIELDS,
  PRIORITY_GUIDE_MAX_LENGTH,
  type PriorityGuide,
} from './evaAutoSendOptions';
import type { Company } from '../list';

const { Text } = Typography;

type ListField =
  | 'eva_helpdesk_auto_send_priorities'
  | 'eva_helpdesk_auto_send_reply_types'
  | 'eva_helpdesk_auto_send_categories';

const NONE_TICKED_TEXT = 'Nothing ticked: Eva sends nothing on her own.';

export interface EvaAutoSendSettingsProps {
  company: Company;
  locked: boolean;
  saver: EvaHelpdeskRepliesFlagProps['saver'];
}

const TickList: React.FC<{
  title: string;
  field: ListField;
  options: ReadonlyArray<{ value: string; label: string }>;
  defaults: string[];
  successText: string;
  /** Shown after the boxes: what can never be ticked, and why. */
  fixed: React.ReactNode;
  company: Company;
  locked: boolean;
  saver: EvaAutoSendSettingsProps['saver'];
}> = ({ title, field, options, defaults, successText, fixed, company, locked, saver }) => {
  const stored = company[field];
  const saved = Array.isArray(stored) ? stored : defaults;
  const pending = saver.pending?.field === field ? (saver.pending.value as string[]) : undefined;
  // What the boxes show (the list being saved, else the saved one); the empty-list line follows it.
  const shown = pending ?? saved;
  return (
    <div role="group" aria-label={title} data-testid={field}>
      <Text strong>{title}</Text>
      <div style={{ marginTop: 4 }}>
        <Checkbox.Group
          value={shown}
          disabled={locked}
          onChange={(next) => {
            if (locked) return;
            // Saved in the list's own order, so the same choice always sends the same list.
            const ticked = options.map((o) => o.value).filter((v) => next.includes(v));
            void saver.save(field, ticked, successText);
          }}
          options={options.map((o) => ({ value: o.value, label: o.label }))}
        />
      </div>
      <div>{fixed}</div>
      {shown.length === 0 && <Text type="secondary">{NONE_TICKED_TEXT}</Text>}
    </div>
  );
};

/** A box that can never be ticked: not part of the saved list, so it can never be sent. */
const NeverBox: React.FC<{ label: string; why: string }> = ({ label, why }) => (
  <Checkbox checked={false} disabled>
    {label} <Text type="secondary">({why})</Text>
  </Checkbox>
);

const guideOf = (company: Company): Required<PriorityGuide> => {
  const g = company.eva_helpdesk_priority_guide;
  return { high: g?.high ?? '', medium: g?.medium ?? '', low: g?.low ?? '' };
};

export const EvaAutoSendSettings: React.FC<EvaAutoSendSettingsProps> = ({
  company,
  locked,
  saver,
}) => {
  const saved = guideOf(company);
  const [guide, setGuide] = useState(saved);
  // A saved (or re-read) description replaces the boxes.
  useEffect(() => {
    setGuide({ high: saved.high, medium: saved.medium, low: saved.low });
  }, [saved.high, saved.medium, saved.low]);
  const next = { high: guide.high.trim(), medium: guide.medium.trim(), low: guide.low.trim() };
  const guideUnchanged =
    next.high === saved.high && next.medium === saved.medium && next.low === saved.low;

  return (
    <Space
      direction="vertical"
      size="middle"
      data-testid="eva-auto-send-settings"
      style={{ marginTop: 8, width: '100%', maxWidth: 560 }}
    >
      <div data-testid="eva-helpdesk-priority-guide">
        {PRIORITY_GUIDE_FIELDS.map((f) => (
          <div key={f.key} style={{ marginBottom: 8 }}>
            <Text strong>{f.label}</Text>
            <Input.TextArea
              aria-label={f.label}
              value={guide[f.key]}
              placeholder={f.todaysRule}
              maxLength={PRIORITY_GUIDE_MAX_LENGTH}
              showCount
              autoSize={{ minRows: 2, maxRows: 6 }}
              disabled={locked}
              onChange={(e) => setGuide((g) => ({ ...g, [f.key]: e.target.value }))}
            />
          </div>
        ))}
        <Text type="secondary">
          Given to Eva on every ticket. Left blank, Eva uses today&apos;s rule, shown in the box.
          Safety issues are always high priority and held for a person.
        </Text>
        <div style={{ marginTop: 4 }}>
          <Button
            disabled={locked || guideUnchanged}
            loading={saver.pending?.field === 'eva_helpdesk_priority_guide'}
            onClick={() =>
              void saver.save('eva_helpdesk_priority_guide', next, 'Priority descriptions saved')
            }
          >
            Save priority descriptions
          </Button>
        </div>
      </div>
      <TickList
        title="Priorities Eva may answer on her own"
        field="eva_helpdesk_auto_send_priorities"
        options={AUTO_SEND_PRIORITY_OPTIONS}
        defaults={DEFAULT_AUTO_SEND_PRIORITIES}
        successText="Priorities Eva may answer on her own saved"
        fixed={<NeverBox label="High" why="never answered automatically" />}
        company={company}
        locked={locked}
        saver={saver}
      />
      <TickList
        title="Reply types Eva may send on her own"
        field="eva_helpdesk_auto_send_reply_types"
        options={AUTO_SEND_REPLY_TYPE_OPTIONS}
        defaults={DEFAULT_AUTO_SEND_REPLY_TYPES}
        successText="Reply types Eva may send on her own saved"
        fixed={<NeverBox label="Apology" why="never sent by Eva, a complaint needs a person" />}
        company={company}
        locked={locked}
        saver={saver}
      />
      <TickList
        title="Categories Eva may answer on her own"
        field="eva_helpdesk_auto_send_categories"
        options={AUTO_SEND_CATEGORY_OPTIONS}
        defaults={DEFAULT_AUTO_SEND_CATEGORIES}
        successText="Categories Eva may answer on her own saved"
        fixed={
          <Text type="secondary">
            Food safety, allergen and legal or media tickets always go to a person.
          </Text>
        }
        company={company}
        locked={locked}
        saver={saver}
      />
    </Space>
  );
};
